import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { ChatConversation, ChatConversationAgent, ChatMessage, ChatAttachmentInfo } from '../services/chat-api.js';
import * as chatApi from '../services/chat-api.js';
import { wsClient } from '../services/websocket-client.js';

// ─── Shared Module State ─────────────────────────────────────────────
// Shared across all ChatStateController instances (module singleton pattern)

type ChatModel = 'sonnet' | 'opus';

let sharedConversationId: number | null = null;
let sharedMessages: ChatMessage[] = [];
let sharedIsStreaming = false;
let sharedConversations: ChatConversation[] = [];
let sharedLoadingState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
let sharedError: string | null = null;
let sharedModel: ChatModel = 'sonnet';
let sharedActiveAgent: ChatConversationAgent | null = null;
let sharedPendingAttachments: ChatAttachmentInfo[] = [];

const hosts = new Set<ReactiveControllerHost>();

function notifyHosts(): void {
  for (const host of hosts) {
    host.requestUpdate();
  }
}

// ─── WebSocket Handler ───────────────────────────────────────────────

function onWsMessage(e: Event): void {
  const detail = (e as CustomEvent).detail as { type: string; payload: Record<string, unknown> };
  const { type, payload } = detail;

  switch (type) {
    case 'chat:token': {
      const convId = payload.conversationId as number;
      if (convId !== sharedConversationId) break;
      const token = payload.text as string;
      const last = sharedMessages[sharedMessages.length - 1];
      // Append token to last assistant message if streaming, otherwise create new
      if (sharedIsStreaming && last && last.role === 'assistant') {
        sharedMessages = [
          ...sharedMessages.slice(0, -1),
          { ...last, content: last.content + token },
        ];
      } else {
        sharedIsStreaming = true;
        sharedMessages = [
          ...sharedMessages,
          {
            id: -1,
            conversation_id: convId,
            role: 'assistant',
            content: token,
            source: 'text',
            created_at: new Date().toISOString(),
          },
        ];
      }
      notifyHosts();
      break;
    }

    case 'chat:complete': {
      const convId = payload.conversationId as number;
      if (convId !== sharedConversationId) break;
      sharedIsStreaming = false;
      notifyHosts();
      break;
    }

    case 'chat:error': {
      const convId = payload.conversationId as number;
      if (convId !== sharedConversationId) break;
      sharedIsStreaming = false;
      sharedError = payload.error as string;
      notifyHosts();
      break;
    }

    case 'chat:title-updated': {
      const convId = payload.conversationId as number;
      const title = payload.title as string;
      sharedConversations = sharedConversations.map(c =>
        c.id === convId ? { ...c, title } : c,
      );
      notifyHosts();
      break;
    }
  }
}

wsClient.addEventListener('ws-message', onWsMessage);

// ─── Controller ──────────────────────────────────────────────────────

export class ChatStateController implements ReactiveController {
  private host: ReactiveControllerHost;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    hosts.add(this.host);
  }

  hostDisconnected(): void {
    hosts.delete(this.host);
  }

  get conversationId(): number | null { return sharedConversationId; }
  get messages(): ChatMessage[] { return sharedMessages; }
  get isStreaming(): boolean { return sharedIsStreaming; }
  get conversations(): ChatConversation[] { return sharedConversations; }
  get loadingState(): 'idle' | 'loading' | 'loaded' | 'error' { return sharedLoadingState; }
  get error(): string | null { return sharedError; }
  get model(): ChatModel { return sharedModel; }
  get activeAgent(): ChatConversationAgent | null { return sharedActiveAgent; }
  get pendingAttachments(): ChatAttachmentInfo[] { return sharedPendingAttachments; }

  async loadConversations(): Promise<void> {
    try {
      sharedConversations = await chatApi.listConversations();
      notifyHosts();
    } catch (e) {
      sharedError = e instanceof Error ? e.message : 'Konversationen konnten nicht geladen werden';
      notifyHosts();
    }
  }

  async loadConversation(id: number): Promise<void> {
    if (sharedLoadingState === 'loading') return;
    sharedLoadingState = 'loading';
    sharedError = null;
    notifyHosts();

    try {
      const data = await chatApi.getConversation(id);
      sharedConversationId = data.id;
      sharedMessages = data.messages;
      sharedIsStreaming = false;
      sharedActiveAgent = data.agent ?? null;
      sharedLoadingState = 'loaded';
    } catch (e) {
      sharedLoadingState = 'error';
      sharedError = e instanceof Error ? e.message : 'Konversation konnte nicht geladen werden';
    }
    notifyHosts();
  }

  async createConversation(agentId?: number): Promise<number> {
    const conv = await chatApi.createConversation(undefined, agentId);
    sharedConversationId = conv.id;
    sharedMessages = [];
    sharedIsStreaming = false;
    sharedActiveAgent = conv.agent ?? null;
    sharedLoadingState = 'loaded';
    sharedConversations = [conv, ...sharedConversations];
    notifyHosts();
    return conv.id;
  }

  async selectConversation(id: number): Promise<void> {
    await this.loadConversation(id);
  }

  async renameConversation(id: number, title: string): Promise<void> {
    try {
      const updated = await chatApi.updateTitle(id, title);
      sharedConversations = sharedConversations.map(c => c.id === id ? updated : c);
      notifyHosts();
    } catch (e) {
      sharedError = e instanceof Error ? e.message : 'Titel konnte nicht aktualisiert werden';
      notifyHosts();
    }
  }

  addPendingAttachment(attachment: ChatAttachmentInfo): void {
    sharedPendingAttachments = [...sharedPendingAttachments, attachment];
    notifyHosts();
  }

  removePendingAttachment(id: number): void {
    sharedPendingAttachments = sharedPendingAttachments.filter(a => a.id !== id);
    notifyHosts();
  }

  clearPendingAttachments(): void {
    sharedPendingAttachments = [];
    notifyHosts();
  }

  async sendMessage(content: string, attachmentIds?: number[]): Promise<void> {
    if (!sharedConversationId || sharedIsStreaming) return;

    // Collect pending attachment IDs if not explicitly provided
    const idsToSend = attachmentIds ?? sharedPendingAttachments.map(a => a.id);

    // Optimistically add user message with attachment info
    const userMsg: ChatMessage = {
      id: -Date.now(),
      conversation_id: sharedConversationId,
      role: 'user',
      content,
      source: 'text',
      created_at: new Date().toISOString(),
    };
    if (sharedPendingAttachments.length > 0) {
      userMsg.attachments = [...sharedPendingAttachments];
    }
    sharedMessages = [...sharedMessages, userMsg];
    sharedIsStreaming = true;
    sharedPendingAttachments = [];
    notifyHosts();

    // Send via WebSocket for streaming
    wsClient.sendChatMessage(sharedConversationId, content, sharedModel, idsToSend.length > 0 ? idsToSend : undefined);
  }

  toggleModel(): void {
    sharedModel = sharedModel === 'sonnet' ? 'opus' : 'sonnet';
    notifyHosts();
  }
}
